<template>
  <div class="split-job-container">
    <basic-form
      ref="jobForm"
      title="Job Info"
      :mode="FormModes.SPARSE"
      :form-params="SplitJobForm"
      :value-change-cb="jobFormChange"
      showClear
    >
      <template v-slot:actions>
        <el-button type="primary" size="small" @click="createSplitJob">
          Create Job
        </el-button>
      </template>
    </basic-form>
    <div class="job-content">
      <split-job-content-create
        :goodsQuery="goodsQuery"
        :palletGroups="palletGroups"
      />
    </div>
    <div class="tabs">
      <el-tabs v-model="selectedTab" @tab-click="selectTabChange">
        <el-tab-pane :name="PalletListTab" label="Pallets">
          <basic-table
            :table-data="palletListPage"
            :table-headers="SplitJobPackingListTableHeaders"
            :has-actions="true"
            :paginationChange="paginationChange"
            :actionWidth="120"
            :showPagination="false"
            :stripe="true"
            :summary="true"
            :selectable="false"
            :hasRowExpand="true"
          >
            <template v-slot:row-actions="props">
              <div class="packing-list-actions f-c">
                <el-button
                  size="small"
                  type="text"
                  @click="removePallet(props.row)"
                >
                  Remove Pallet
                </el-button>
                <el-button
                  size="small"
                  type="text"
                  @click="deleteGroup(props.row)"
                >
                  Delete Group
                </el-button>
              </div>
            </template>
          </basic-table>
        </el-tab-pane>
        <el-tab-pane :name="PackingListTab" label="Packing List">
          <packing-list
            v-if="goodsQuery"
            :queryParams="goodsQuery"
            :showActions="false"
            :showHeaderOps="false"
          />
        </el-tab-pane>
      </el-tabs>
    </div>
  </div>
</template>
<script>
// components
import BasicForm from '@/components/common/BasicForm.vue'
import BasicTable from '@/components/common/BasicTable.vue'
import PackingList from '@/components/goods/PackingList.vue'
import SplitJobContentCreate from '@/components/jobs/SplitJobContentCreate.vue'
// models
import {
  FormModes,
  getDefaultSort,
  PageCurrentKey,
  PageSizeKey
} from '@/core/configs/types'
import {
  SplitJobForm,
  SplitJobCreate,
  SplitJobModify,
  SplitJobPackingListTableHeaders
} from '@/core/models/job'
// apis
import { queryPallets } from '@/api/pallet'
// others
import cloneDeep from 'lodash/cloneDeep'
import isEmpty from 'lodash/isEmpty'
import { queryOrders } from '@/api/order'
// mixins
import { pageJumps } from '@/core/mixin/pages'
import { TabsMixin } from '@/core/mixin/tabs'

export default {
  name: 'PalletSplit',
  components: {
    BasicForm,
    BasicTable,
    PackingList,
    SplitJobContentCreate
  },
  mixins: [TabsMixin, pageJumps],
  data() {
    return {
      FormModes,
      SplitJobForm,
      SplitJobPackingListTableHeaders,
      jobForm: cloneDeep(SplitJobForm),
      loadingPackingList: false,
      curGroup: '',
      palletGroups: [],
      palletList: [],
      packingList: [],
      packingListTableHeaders: [],
      pagination: {
        [PageSizeKey]: -1,
        [PageCurrentKey]: 1,
        total: 0,
        asc: '',
        desc: ''
      }
    }
  },
  mounted() {
    if (this.pageMode === SplitJobCreate) {
      // load pallets from cartlist
      const cartList = JSON.parse(localStorage.getItem('cartList', ''))
      this.$set(this, 'palletGroups', cartList)
      this.fetchPalletData()
    } else if (this.pageMode === SplitJobModify) {
      // get pallets based on job id
    }
  },
  computed: {
    selectedTab: {
      get() {
        return this.$route.query.tab
      },
      set(value) {
        this.goToPalletSplitJob({ ...this.$route.query, tab: value })
      }
    },
    pageMode() {
      return this.$route.query.mode
    },
    selectedPalletIds() {
      return this.palletGroups.reduce(
        (prev, cur) => [...prev, ...cur.palletIds],
        []
      )
    },
    selectedGoodsIds() {
      return this.palletGroups.reduce(
        (prev, cur) => [...prev, ...cur.goodsIds],
        []
      )
    },
    selectedOrderIds() {
      return this.palletGroups.map(one => one.orderId)
    },
    palletGroupsMap() {
      return Object.fromEntries(this.palletGroups.map(one => [one.id, one]))
    },
    palletListPage() {
      return {
        records: this.palletList,
        ...this.pagination
      }
    },
    palletQuery() {
      return {
        ids: this.selectedPalletIds,
        ...this.pagination
      }
    },
    orderQuery() {
      return {
        ids: this.selectedOrderIds,
        size: -1
      }
    },
    goodsQuery() {
      if (this.selectedGoodsIds.length === 0) return {}
      return {
        ids: this.selectedGoodsIds,
        size: -1
      }
    }
  },
  methods: {
    jobFormChange() {},
    createSplitJob() {
      // create split job
    },
    paginationChange() {
      //
    },
    removePallet() {
      //
    },
    deleteGroup() {
      //
    },
    selectTabChange() {},
    groupsChanged() {
      //
    },
    async fetchPalletData() {
      this.loadingPackingList = true
      getDefaultSort(SplitJobPackingListTableHeaders, this.pagination)
      let page
      if (!isEmpty(this.palletQuery)) {
        page = await queryPallets(this.palletQuery)
      }
      const pallets = page?.records ?? []
      const orderPage = await queryOrders(this.orderQuery)
      const ordersMap = Object.fromEntries(
        orderPage.records.map(one => [one.id, one])
      )
      this.palletGroups.forEach(group => {
        group.order = ordersMap[group.orderId]
      })
      // assign order to each pallet
      pallets.forEach(pallet => {
        const key = `${pallet.orderId}/${pallet.addressId}/${pallet.groupName}`
        pallet.group = this.palletGroupsMap[key]
        pallet.riiOrderNo = pallet.group.riiOrderNo
      })
      this.$set(this, 'palletList', pallets)
      this.loadingPackingList = false
    }
  }
}
</script>
<style lang="scss" scoped>
@import '@/assets/styles/common.scss';
.split-job-container {
  display: flex;
  flex-direction: column;
  .job-content {
  }
  .packing-list-actions {
    gap: 10px;
    button {
      margin: 0;
      padding: 0;
    }
  }
  .job-content-card {
  }
  .tabs {
    @extend .global-margin;
    @extend .shadow-level-three;

    border-radius: $home-card-border-radius;
    padding: $home-card-padding-y $home-card-padding-x;
    background-color: white;
  }
}
</style>
